// ============================================================================
// WIDGETS API — React Query hooks for widget endpoints
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import type { Widget } from '@/types/views'

const CALENDAR_WEEK_WIDGET_KEY = ['calendar-week-widget']

export function useWidgets() {
  return useQuery({
    queryKey: ['widgets'],
    queryFn: async () => {
      const { data } = await apiClient.get<Widget[]>('/widgets/')
      return data
    },
  })
}

/** Fetch one widget by id (standard DRF detail route). Used by the chat's
 *  InsightChartCard when a `chart` render arrives without inline code. */
export function useWidget(widgetId: number | string | null | undefined) {
  return useQuery({
    queryKey: ['widgets', String(widgetId)],
    queryFn: async () => {
      const { data } = await apiClient.get<Widget>(`/widgets/${widgetId}/`)
      return data
    },
    enabled: widgetId != null && widgetId !== '',
  })
}

export function useWidgetLibrary() {
  return useQuery({
    queryKey: ['widget-library'],
    queryFn: async () => {
      const { data } = await apiClient.get<Widget[]>('/widgets/library/')
      return data
    },
  })
}

// Calendar week-summary slot — one pinned widget per profile (widgets/services.py
// backed by a reserved View/ViewWidget, not a new column; shared with Flow + MCP).
export function useCalendarWeekWidget() {
  return useQuery({
    queryKey: CALENDAR_WEEK_WIDGET_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<{ widget: Widget | null }>('/calendar-week-widget/')
      return data.widget
    },
  })
}

export function useSetCalendarWeekWidget() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (widgetId: number) => {
      const { data } = await apiClient.put<{ widget: Widget }>('/calendar-week-widget/', {
        widget_id: widgetId,
      })
      return data.widget
    },
    onSuccess: (widget) => {
      queryClient.setQueryData(CALENDAR_WEEK_WIDGET_KEY, widget)
    },
  })
}

export function useClearCalendarWeekWidget() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/calendar-week-widget/')
    },
    onSuccess: () => {
      queryClient.setQueryData(CALENDAR_WEEK_WIDGET_KEY, null)
    },
  })
}
